import { ChangeDetectionStrategy, Component, Input, inject } from "@angular/core";
import { DialogShellComponent } from "src/app/components/shared/dialog-shell/dialog-shell.component";
import { Router } from "@angular/router";
import { IonIcon, ModalController } from "@ionic/angular";
import { addIcons } from "ionicons";
import { checkmarkOutline } from "ionicons/icons";
import { UserHolding } from "src/app/account/account.model";

/**
 * The desktop holding selector. The holding scopes every screen in the product, so the control
 * that switches it is a designed dialog rather than a stock alert with CANCEL and OK. Dismisses
 * with the chosen holding as data and role `select`.
 */
@Component({
  selector: "app-holding-select-modal",
  standalone: true,
  imports: [IonIcon, DialogShellComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-dialog-shell
      title="Switch holding"
      subtitle="Everything on screen belongs to the holding you choose."
      (closeRequest)="close()"
    >
      <ul class="ff-holdings__list" role="listbox" aria-label="Holdings">
        @for (h of holdings; track h.id) {
          <li>
            <button
              type="button"
              role="option"
              class="ff-holding"
              [class.ff-holding--current]="h.id === selectedId"
              [attr.aria-selected]="h.id === selectedId"
              (click)="choose(h)"
            >
              <span class="ff-holding__avatar" aria-hidden="true">{{ h.holdingName.charAt(0).toUpperCase() }}</span>
              <span class="ff-holding__text">
                <span class="ff-holding__name">{{ h.holdingName }}</span>
                <span class="ff-holding__meta flock-tabular-nums">
                  {{ h.cphNumber ? "CPH " + h.cphNumber : "No CPH number" }}{{ h.ownerType === "contributor" ? " · Contributor" : "" }}
                </span>
              </span>
              @if (h.id === selectedId) {
                <ion-icon name="checkmark-outline" class="ff-holding__tick" aria-label="Current holding"></ion-icon>
              }
            </button>
          </li>
        }
      </ul>
      <footer dialogFooter class="ff-dialog__foot">
        <button type="button" class="ff-dialog__link" (click)="manage()">Manage my farms</button>
      </footer>
    </app-dialog-shell>
  `,
  styles: [
    `
      .ff-holdings__list {
        list-style: none;
        margin: 0;
        padding: 0 8px;
        max-height: 60vh;
        overflow: auto;
      }
      .ff-holding {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 12px;
        border: 0;
        border-radius: 10px;
        background: transparent;
        color: inherit;
        font: inherit;
        text-align: left;
        cursor: pointer;
      }
      .ff-holding:hover {
        background: var(--flock-surface-hover);
      }
      .ff-holding:focus-visible {
        outline: 2px solid var(--flock-focus-ring);
        outline-offset: -2px;
      }
      .ff-holding--current {
        background: var(--flock-surface-selected);
      }
      .ff-holding__avatar {
        width: 36px;
        height: 36px;
        flex: none;
        border-radius: 50%;
        background: var(--ion-color-success);
        color: var(--ion-color-success-contrast);
        font-weight: 800;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .ff-holding__text {
        display: flex;
        flex-direction: column;
        min-width: 0;
        flex: 1;
      }
      .ff-holding__name {
        font-weight: 700;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .ff-holding__meta {
        font-size: 13px;
        color: var(--flock-text-muted);
      }
      .ff-holding__tick {
        font-size: 20px;
        color: var(--ion-color-green);
        flex: none;
      }
    `,
  ],
})
export class HoldingSelectModalComponent {
  private readonly modalCtrl = inject(ModalController);
  private readonly router = inject(Router);

  @Input() holdings: UserHolding[] = [];
  @Input() selectedId: number | null = null;

  constructor() {
    addIcons({ checkmarkOutline });
  }

  choose(holding: UserHolding) {
    this.modalCtrl.dismiss(holding, "select");
  }

  close() {
    this.modalCtrl.dismiss(null, "cancel");
  }

  manage() {
    this.modalCtrl.dismiss(null, "cancel").then(() => this.router.navigate(["/manage/holdings"]));
  }
}
